import { useMemo } from 'react';
import { Image as DSImage, ImageLoadings } from '@picsart/design-system/components/Image';
import { useTranslator } from '@picsart/rc/services/localization';
import classNames from 'classnames';
import { InView } from 'react-intersection-observer';

import type { ImageType } from 'components/Image/types';
import { ActionTypes, ImageSizes, FetchPriority } from 'constants/enums';
import { FittingOptions } from '@picsart/growth-rc/old/MediaComponents';
import optimizeImage from 'utils/optimizeImage';
import getImageQuality from 'utils/getImageQuality';

import useStyles from './styles';

/**
 * Image wrapper component for the DS Image component that utilizes the IntersectionObserver API
 * to lazy load the image when it comes into view.
 *
 * @param src - The source of the image
 * @param alt - The alt text of the image
 * @param className - The class name of the image container
 * @param imageClassName - The class name of the image
 * @param quality - The quality of the image (used in CDN ?q= param) (0-100)
 * @param resize - The target height of the image in pixels (used in CDN ?r= param) (400, 800, 1200)
 * @param action - The action of the image (used in CDN ?to= param) (min, max, crop, fixed)
 * @param aspectRatio - The aspect ratio of the image (1:1, 16:9, 4:3, etc.)
 * @param objectFit - The object fit of the image (contain, cover, fill, none)
 * @param padding - The padding of the image (large, medium, small, none, stretch)
 * @param priority - The priority of the image (true, false)
 * @param preloadOffset - The preload offset of the image (0px, 100px, 200px, etc.)
 */
function Image({
  src,
  alt = '',
  className,
  imageClassName,
  quality,
  resize = 400,
  action = ActionTypes.min,
  aspectRatio,
  objectFit = FittingOptions[FittingOptions.Contain],
  padding,
  priority = false,
  preloadOffset = '0px 0px 200px 0px',
}: ImageType) {
  const styles = useStyles({ objectFit, padding, aspectRatio, priority });
  const translate = useTranslator();
  const imageQuality = useMemo(() => quality || getImageQuality(), [quality]);
  const imgClassNames = classNames(imageClassName, styles.imgClassName);

  return (
    <InView rootMargin={preloadOffset} threshold={0.5} triggerOnce>
      {({ inView, ref }) => (
        <div ref={ref} className={className} data-testid="image-container">
          <picture>
            <source
              media={`(min-width: ${ImageSizes.DESKTOP_BREAKPOINT}px)`}
              srcSet={`${optimizeImage(
                src,
                action,
                ImageSizes.DESKTOP_BREAKPOINT.toString(),
                imageQuality,
              )}`}
            />
            <source
              media={`(min-width: ${ImageSizes.TABLET_BREAKPOINT}px)`}
              srcSet={`${optimizeImage(
                src,
                action,
                ImageSizes.TABLET_BREAKPOINT.toString(),
                imageQuality,
              )}`}
            />
            <DSImage
              className={imgClassNames}
              src={optimizeImage(
                src,
                action,
                resize.toString() || ImageSizes.MOBILE_BREAKPOINT.toString(),
                imageQuality,
              )}
              alt={translate(alt)}
              loading={priority || inView ? ImageLoadings.Eager : ImageLoadings.Lazy}
              // @ts-expect-error fetchPriority is not typed in the DSImage component
              fetchPriority={priority ? FetchPriority.High : FetchPriority.Low}
            />
          </picture>
        </div>
      )}
    </InView>
  );
}

export default Image;
